import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Clock, Pencil, Plus, Trash2, RotateCcw, Lock, Unlock } from "lucide-react";

const ACTION_META = {
  create: { icon: Plus, label: "Created", color: "text-[#2d5e1f] bg-[#e8f6e2] border-[#6cc850]" },
  update: { icon: Pencil, label: "Updated", color: "text-[#39678f] bg-[#e6f3ff] border-[#39678f]" },
  lock: { icon: Lock, label: "Locked", color: "text-[#4B4B9D] bg-[#ececf7] border-[#4B4B9D]" },
  unlock: { icon: Unlock, label: "Unlocked", color: "text-[#8a6d00] bg-[#f5ead0] border-[#8a6d00]" },
  delete: { icon: Trash2, label: "Soft-deleted", color: "text-[#b30035] bg-[#fbe4ea] border-[#b30035]" },
  restore: { icon: RotateCcw, label: "Restored", color: "text-[#2d5e1f] bg-[#e8f6e2] border-[#6cc850]" },
};

const FIELD_LABELS = {
  job_number: "Job/Freezer №",
  vessel_type: "Vessel type",
  vessel_number: "Vessel №",
  test_result: "Test result",
  test_pressure: "Test pressure",
  test_medium: "Test medium",
  test_person_id: "Test person",
  test_date: "Test date",
  failure_points: "Failure points",
  welder1_id: "Welder 1",
  welder2_id: "Welder 2",
  blown_out_employee_id: "Blown out by",
  blown_out_date: "Blown out date",
  notes: "Notes",
};

function fmt(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return String(v);
}

export default function AuditLog({ entityId }) {
  const [entries, setEntries] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!entityId) return;
    api
      .get("/audit-log", { params: { entity_id: entityId, limit: 50 } })
      .then((res) => setEntries(res.data))
      .catch(() => setEntries([]));
  }, [entityId]);

  if (!entityId) return null;
  const count = entries?.length || 0;

  return (
    <div className="border-t border-[#33424f]/20 p-6" data-testid="audit-log-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between group"
      >
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-[#39678f]" />
          <div className="text-left">
            <div className="eyebrow">/ Audit Trail</div>
            <div className="font-display text-lg text-[#33424f] tracking-tight">
              {count} EVENT{count === 1 ? "" : "S"}
            </div>
          </div>
        </div>
        <div className="text-xs subtitle uppercase tracking-wider text-[#39678f] group-hover:underline">
          {open ? "Hide" : "Show history"}
        </div>
      </button>

      {open && (
        <div className="mt-5 space-y-3" data-testid="audit-entries">
          {entries === null && <div className="text-sm text-[#50493b] font-body">Loading…</div>}
          {entries && entries.length === 0 && (
            <div className="text-sm text-[#50493b] font-body">No audit history yet.</div>
          )}
          {entries &&
            entries.map((e) => {
              const meta = ACTION_META[e.action] || ACTION_META.update;
              const Icon = meta.icon;
              const changeKeys = Object.keys(e.changes || {});
              return (
                <div
                  key={e.id}
                  className="border border-[#33424f]/15 bg-white p-4"
                  data-testid={`audit-entry-${e.action}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
                      <div className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10px] subtitle uppercase tracking-[0.2em] ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </div>
                      <div className="font-body text-sm text-[#33424f] truncate">
                        by <span className="subtitle">{e.actor_name || "—"}</span>
                      </div>
                    </div>
                    <div className="font-body text-xs text-[#50493b] tabular-nums shrink-0">
                      {new Date(e.timestamp).toLocaleString()}
                    </div>
                  </div>
                  {changeKeys.length > 0 && (
                    <ul className="mt-3 space-y-2 sm:space-y-1 text-xs font-body">
                      {changeKeys.map((k) => (
                        <li
                          key={k}
                          className="flex flex-col sm:grid sm:grid-cols-[140px,1fr] sm:gap-3 gap-1"
                        >
                          <span className="subtitle text-[#50493b] uppercase tracking-[0.15em] text-[10px]">
                            {FIELD_LABELS[k] || k}
                          </span>
                          <span className="text-[#33424f] break-words">
                            <span className="line-through text-[#b30035]/80">{fmt(e.changes[k].old)}</span>
                            <span className="mx-2 text-[#50493b]">→</span>
                            <span className="subtitle">{fmt(e.changes[k].new)}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
